import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

interface Invoice {
  id: number
  invoice_number: string
  currency: string
  currency_symbol: string
}

interface LineItem {
  id: number
  description: string
  quantity: number | string
  unit_price: number | string
  user_id: number | string | null
  discount_percentage: number | string | null
  tax_percentage: number | string | null
}

interface Employee {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  invoice: Invoice
  line_item: LineItem
  employees: Employee[]
}

export default function InvoiceLineItemEdit() {
  const { invoice, line_item, employees } = usePage<Props>().props

  const { data, setData, patch, processing, errors } = useForm({
    description: line_item.description || "",
    quantity: String(line_item.quantity || "1"),
    unit_price: String(line_item.unit_price || ""),
    user_id: line_item.user_id ? String(line_item.user_id) : "",
    discount_percentage: String(line_item.discount_percentage || ""),
    tax_percentage: String(line_item.tax_percentage || ""),
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/invoices/${invoice.id}/invoice_line_items/${line_item.id}`)
  }

  return (
    <>
      <Head title={`Edit Line Item - ${invoice.invoice_number} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/invoices">Invoices</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={`/invoices/${invoice.id}`}>{invoice.invoice_number}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit Line Item</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/invoices/${invoice.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Edit Line Item</h1>
          </div>
        </div>

        <div className="max-w-lg">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      rows={3}
                      value={data.description}
                      onChange={(e) => setData("description", e.target.value)}
                      placeholder="Describe the line item..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user_id">
                      Employee <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Select value={data.user_id} onValueChange={(v) => setData("user_id", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={String(emp.id)}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="quantity">Quantity</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="0"
                        step="0.01"
                        value={data.quantity}
                        onChange={(e) => setData("quantity", e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="unit_price">Unit Price ({invoice.currency_symbol})</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={data.unit_price}
                        onChange={(e) => setData("unit_price", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="discount_percentage">Discount %</Label>
                      <Input
                        id="discount_percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={data.discount_percentage}
                        onChange={(e) => setData("discount_percentage", e.target.value)}
                        placeholder="0"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tax_percentage">Tax %</Label>
                      <Input
                        id="tax_percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={data.tax_percentage}
                        onChange={(e) => setData("tax_percentage", e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href={`/invoices/${invoice.id}`}>Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Update Line Item"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

InvoiceLineItemEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
